import { RiskStatus } from '@app/src/types/resourceExplorer'
import type {
  ProviderRiskReasonDetails,
  RiskReasonTranslation,
  SustainabilityTopicProviderRisk,
  SustainabilityTopicRisk,
} from '@app/src/types/topicalRisk'
import { RiskChangeReason } from '@app/src/types/topicalRisk'
import { getAnswerFlagDescription } from '@app/src/utils/answerFlagUtils'

// ============================================================================
// Core Types
// ============================================================================

interface ChangedRiskIndex {
  riskTypeName?: string
  isCountryRisk: boolean
}

type TopicFilter = (current: SustainabilityTopicRisk, previous: SustainabilityTopicRisk | undefined) => boolean

// ============================================================================
// Pure Functions - Main API
// ============================================================================

/**
 * Infers the reason for risk change by analyzing differences between snapshots.
 * Returns the most specific reason that can be determined from the data.
 */
export function inferRiskChangeReason(
  current: SustainabilityTopicProviderRisk | null,
  previous: SustainabilityTopicProviderRisk | null,
): RiskChangeReason | null {
  if (!current) return null
  if (!previous) return RiskChangeReason.InitialCalculation

  const orgChange = detectOrganizationChange(current, previous)
  if (orgChange) return orgChange

  if (findToggledRiskIndex(current, previous)) {
    return RiskChangeReason.RiskTypesToggled
  }

  if (findUpdatedRiskIndex(current, previous)) {
    return RiskChangeReason.RiskTypesUpdated
  }

  if (hasAnswerFlagRiskStatusChanged(current, previous)) {
    return RiskChangeReason.AnswerFlagged
  }

  const assessmentChangeResult = findFirstAssessmentChange(current, previous)
  if (assessmentChangeResult) {
    switch (assessmentChangeResult.type) {
      case 'enabled':
        return RiskChangeReason.AssessmentEnabled
      case 'disabled':
        return RiskChangeReason.AssessmentDisabled
      case 'deleted':
        return RiskChangeReason.AssessmentDeleted
      case 'calculated':
        return RiskChangeReason.AssessmentCalculated
    }
  }

  if (hasAnyTopicRiskStatusChanged(current, previous)) {
    return RiskChangeReason.MethodologyUpdated
  }

  return null
}

/**
 * Computes affected entity details by comparing two ProviderRisk snapshots.
 */
export function computeRiskReasonDetails(
  reason: RiskChangeReason | null,
  current: SustainabilityTopicProviderRisk | null,
  previous: SustainabilityTopicProviderRisk | null,
): ProviderRiskReasonDetails | null {
  if (!reason || !current) return null

  switch (reason) {
    case RiskChangeReason.InitialCalculation:
      return null

    case RiskChangeReason.RiskTypesToggled:
      return { affectedTopicNames: getChangedTopicNames(current, previous) }

    case RiskChangeReason.RiskTypesUpdated: {
      const updatedRiskIndex = previous ? findUpdatedRiskIndex(current, previous) : null
      return {
        riskIndexName: updatedRiskIndex?.riskTypeName,
        isCountryRisk: updatedRiskIndex?.isCountryRisk,
        affectedTopicNames: getChangedTopicNames(current, previous),
      }
    }

    case RiskChangeReason.AssessmentEnabled:
    case RiskChangeReason.AssessmentDisabled:
    case RiskChangeReason.AssessmentDeleted:
    case RiskChangeReason.AssessmentCalculated: {
      const changedTopics = getTopicsWithAssessmentChange(current, previous, reason)
      return {
        assessmentName: changedTopics[0]?.assessmentName,
        affectedTopicNames: changedTopics.map(t => t.topicName).filter((n): n is string => !!n),
      }
    }

    case RiskChangeReason.CountryUpdated:
    case RiskChangeReason.IndustryUpdated:
    case RiskChangeReason.CountryAndIndustryUpdated:
      return { affectedTopicNames: getChangedTopicNames(current, previous) }

    case RiskChangeReason.AnswerFlagged: {
      const ruleDescriptions = extractAnswerFlagRuleDescription(current)
      return {
        answerFlagNames: ruleDescriptions,
      }
    }

    default:
      return null
  }
}

/**
 * Returns translation key and values for a risk reason.
 */
export function getRiskReasonTranslation(
  reason: RiskChangeReason,
  details: ProviderRiskReasonDetails | null,
): RiskReasonTranslation | null {
  const topics = formatTopicList(details?.affectedTopicNames ?? [])
  const assessmentName = details?.assessmentName ?? 'topicalRisk.riskReason.fallback.assessmentName'

  switch (reason) {
    case RiskChangeReason.InitialCalculation:
      return null

    case RiskChangeReason.RiskTypesToggled:
      return {
        messageKey: 'topicalRisk.riskReason.riskTypesToggled',
        values: { topics },
      }

    case RiskChangeReason.RiskTypesUpdated: {
      const messageKey = details?.isCountryRisk
        ? 'topicalRisk.riskReason.riskTypesUpdatedCountry'
        : 'topicalRisk.riskReason.riskTypesUpdatedIndustry'
      return {
        messageKey,
        values: { riskIndexName: details?.riskIndexName ?? 'topicalRisk.riskReason.fallback.riskIndexName' },
      }
    }

    case RiskChangeReason.AssessmentEnabled:
      return {
        messageKey: 'topicalRisk.riskReason.assessmentEnabled',
        values: { assessmentName, topics },
      }

    case RiskChangeReason.AssessmentDisabled:
      return {
        messageKey: 'topicalRisk.riskReason.assessmentDisabled',
        values: { assessmentName, topics },
      }

    case RiskChangeReason.AssessmentDeleted:
      return {
        messageKey: 'topicalRisk.riskReason.assessmentDeleted',
        values: { assessmentName, topics },
      }

    case RiskChangeReason.AssessmentCalculated:
      return {
        messageKey: 'topicalRisk.riskReason.assessmentCalculated',
        values: { assessmentName, topics },
      }

    case RiskChangeReason.CountryUpdated:
      return {
        messageKey: 'topicalRisk.riskReason.countryUpdated',
        values: { topics },
      }

    case RiskChangeReason.IndustryUpdated:
      return {
        messageKey: 'topicalRisk.riskReason.industryUpdated',
        values: { topics },
      }

    case RiskChangeReason.CountryAndIndustryUpdated:
      return {
        messageKey: 'topicalRisk.riskReason.countryAndIndustryUpdated',
        values: { topics },
      }

    case RiskChangeReason.AnswerFlagged: {
      const flaggedAnswers = formatTopicList(details?.answerFlagNames ?? [])
      const count = details?.answerFlagNames?.length ?? 0

      // If no valid flags (all deleted/superseded), show "criteria no longer met"
      if (count === 0) {
        return {
          messageKey: 'topicalRisk.riskReason.flagCriteriaNoLongerMet',
          values: {},
        }
      }

      return {
        messageKey: 'topicalRisk.riskReason.answerFlagged',
        values: {
          count: count.toString(),
          flaggedAnswers,
        },
      }
    }

    case RiskChangeReason.MethodologyUpdated:
      return {
        messageKey: 'topicalRisk.riskReason.methodologyUpdated',
        values: {},
      }

    default:
      return null
  }
}

/**
 * Finds the previous snapshot in a time-ordered list.
 * Assumes snapshots are ordered by createdAt descending (newest first).
 */
export function findPreviousSnapshot(
  snapshots: SustainabilityTopicProviderRisk[],
  currentId: number,
): SustainabilityTopicProviderRisk | null {
  const currentIndex = snapshots.findIndex(s => s.id === currentId)
  if (currentIndex === -1 || currentIndex === snapshots.length - 1) return null
  return snapshots[currentIndex + 1] ?? null
}

// ============================================================================
// Timeline Processing
// ============================================================================

/**
 * Processes provider risk history into timeline and chart data.
 * Pure function - no side effects, just data transformation.
 *
 * Filters snapshots to only include those with meaningful changes,
 * then enriches them with reason and description information.
 *
 * @param providerRiskHistory - Array of risk snapshots, ordered newest first
 * @returns Processed timeline and chart items
 */
export function processRiskTimeline(providerRiskHistory: SustainabilityTopicProviderRisk[]) {
  const itemsWithReason = providerRiskHistory.flatMap(risk => {
    const previous = findPreviousSnapshot(providerRiskHistory, risk.id)
    const reason = inferRiskChangeReason(risk, previous)
    // Include item if there's a reason and either topic risk or answer flag risk changed
    const hasChange = hasAnyTopicRiskStatusChanged(risk, previous) || hasAnswerFlagRiskStatusChanged(risk, previous)
    if (!reason || !hasChange) return []
    return [{ risk, previous, reason }]
  })

  const timelineItems = itemsWithReason.map(({ risk, previous, reason }) => {
    const details = computeRiskReasonDetails(reason, risk, previous)

    return {
      createdAt: risk.createdAt,
      riskStatus: risk.riskStatus as RiskStatus,
      descriptionTranslation: getRiskReasonTranslation(reason, details),
      isInitialCalculation: reason === RiskChangeReason.InitialCalculation,
    }
  })

  const chartItems = itemsWithReason.map(({ risk }) => ({
    createdAt: risk.createdAt,
    riskStatus: risk.riskStatus as RiskStatus,
  }))

  return { timelineItems, chartItems }
}

// ============================================================================
// Topic Filtering Predicates
// ============================================================================

function hasRiskStatusChanged(current: SustainabilityTopicRisk, prev: SustainabilityTopicRisk | undefined): boolean {
  return !prev || prev.totalRiskStatus !== current.totalRiskStatus
}

/**
 * Assessment change result - contains both the change type and which assessment changed.
 * Since only one assessment can change at a time per topic, we track which one.
 */
interface AssessmentChange {
  type: 'enabled' | 'disabled' | 'deleted' | 'calculated' | 'none'
  source: 'direct' | 'section' | null
}

/**
 * Analyzes a single assessment type (direct or section) for changes.
 * Returns the change type or 'none' if no change detected.
 *
 * Uses totalRiskStatus to distinguish between:
 * - 'disabled': Template deactivated in settings (GDeactivated status)
 * - 'deleted': Result deleted but template still active (ANotApplicable status)
 */
function detectAssessmentTypeChange(
  prevTemplateId: number | undefined,
  currentTemplateId: number | undefined,
  prevLevel: number | undefined,
  currentLevel: number | undefined,
  prevTotalRiskStatus: RiskStatus | undefined,
  currentTotalRiskStatus: RiskStatus | undefined,
): AssessmentChange['type'] {
  const templateToggled = !!prevTemplateId !== !!currentTemplateId
  const levelEnabled = prevLevel === undefined && currentLevel !== undefined
  const levelDisabled = prevLevel !== undefined && currentLevel === undefined
  const levelChanged = prevLevel !== currentLevel

  if (templateToggled && levelEnabled) return 'enabled'
  if (templateToggled && levelDisabled) return 'disabled'

  if (levelDisabled) {
    // Level went undefined - check risk status to determine why
    // GDeactivated = template disabled in settings
    // ANotApplicable = template still active, but result deleted
    const isTemplateDeactivated = currentTotalRiskStatus === RiskStatus.Deactivated
    return isTemplateDeactivated ? 'disabled' : 'deleted'
  }

  if (levelChanged && !templateToggled) return 'calculated'

  return 'none'
}

/**
 * Analyzes assessment changes between two topic states.
 * Returns both the change type and which assessment (direct vs section) changed.
 *
 * Each topic can have two types of assessments:
 * - Direct: assessmentTemplateId + assessmentLevel
 * - Section: assessmentTemplateSectionId + assessmentSectionLevel
 *
 * IMPORTANT: Only one assessment can change at a time per topic (platform constraint).
 */
function getAssessmentChange(
  current: SustainabilityTopicRisk,
  prev: SustainabilityTopicRisk | undefined,
): AssessmentChange {
  // Check direct assessment first (higher priority)
  const directChange = detectAssessmentTypeChange(
    prev?.assessmentTemplateId,
    current.assessmentTemplateId,
    prev?.assessmentLevel,
    current.assessmentLevel,
    prev?.totalRiskStatus,
    current.totalRiskStatus,
  )
  if (directChange !== 'none') return { type: directChange, source: 'direct' }

  // Check section assessment (lower priority)
  const sectionChange = detectAssessmentTypeChange(
    prev?.assessmentTemplateSectionId,
    current.assessmentTemplateSectionId,
    prev?.assessmentSectionLevel,
    current.assessmentSectionLevel,
    prev?.totalRiskStatus,
    current.totalRiskStatus,
  )
  if (sectionChange !== 'none') return { type: sectionChange, source: 'section' }

  return { type: 'none', source: null }
}

/**
 * Finds the first topic with an assessment change and returns the change info.
 * Returns null if no assessment changes found.
 */
function findFirstAssessmentChange(
  current: SustainabilityTopicProviderRisk,
  previous: SustainabilityTopicProviderRisk,
): AssessmentChange | null {
  const currentTopics = getAllTopicRisks(current)
  const previousTopicsMap = buildTopicMap(previous)

  for (const topic of currentTopics) {
    const prevTopic = previousTopicsMap.get(topic.sustainabilityTopicId)
    const change = getAssessmentChange(topic, prevTopic)
    if (change.type !== 'none') return change
  }

  return null
}

// ============================================================================
// Topic Filtering
// ============================================================================

function filterTopics(
  current: SustainabilityTopicProviderRisk,
  previous: SustainabilityTopicProviderRisk | null,
  predicate: TopicFilter,
): SustainabilityTopicRisk[] {
  const currentTopics = getAllTopicRisks(current)
  const previousTopicsMap = buildTopicMap(previous)

  return currentTopics.filter(topic => {
    const prev = previousTopicsMap.get(topic.sustainabilityTopicId)
    return predicate(topic, prev)
  })
}

/**
 * Extracts all topic risks from a provider risk, flattening the nested structure.
 * Returns empty array if provider risk is null or has no area risks.
 */
export function getAllTopicRisks(providerRisk: SustainabilityTopicProviderRisk | null): SustainabilityTopicRisk[] {
  if (!providerRisk?.sustainabilityAreaRisks) return []
  return providerRisk.sustainabilityAreaRisks.flatMap(areaRisk => areaRisk.sustainabilityTopicRisks ?? [])
}

/**
 * Extracts unique topic names from an array of topic risks.
 * Filters out null/undefined names and removes duplicates.
 */
function extractUniqueTopicNames(topics: SustainabilityTopicRisk[]): string[] {
  const names = topics.map(t => t.sustainabilityTopic?.name).filter((name): name is string => !!name)
  return [...new Set(names)]
}

/**
 * Returns the rule description from each answer flag result.
 * Filters out deleted/superseded flags (those without a valid rule).
 */
function extractAnswerFlagRuleDescription(providerRisk: SustainabilityTopicProviderRisk): string[] {
  if (!providerRisk.answerFlagRuleResults) return []
  return providerRisk.answerFlagRuleResults
    .filter(result => result.answerFlagRule != null)
    .map(getAnswerFlagDescription)
}

/**
 * Formats a list of topic names into a human-readable string with proper grammar.
 * Uses "&" as the final separator for better readability.
 */
function formatTopicList(topics: string[]): string {
  if (topics.length === 0) return ''
  if (topics.length === 1) return topics[0]

  const allButLast = topics.slice(0, -1).join(', ')
  const last = topics[topics.length - 1]
  return `${allButLast} & ${last}`
}

/**
 * Checks if any topic's risk status changed between two provider risk snapshots.
 */
function hasAnyTopicRiskStatusChanged(
  current: SustainabilityTopicProviderRisk,
  previous: SustainabilityTopicProviderRisk | null,
): boolean {
  if (!previous) return true
  const currentTopics = getAllTopicRisks(current)
  const previousMap = buildTopicMap(previous)
  for (const topic of currentTopics) {
    const prev = previousMap.get(topic.sustainabilityTopicId)
    if (!prev || prev.totalRiskStatus !== topic.totalRiskStatus) return true
  }
  return false
}

/**
 * Checks if the answer flag risk status changed between two provider risk snapshots.
 */
function hasAnswerFlagRiskStatusChanged(
  current: SustainabilityTopicProviderRisk,
  previous: SustainabilityTopicProviderRisk | null,
): boolean {
  if (!previous) return false
  return current.answerFlagRiskStatus !== previous.answerFlagRiskStatus
}

function buildTopicMap(providerRisk: SustainabilityTopicProviderRisk | null): Map<number, SustainabilityTopicRisk> {
  return new Map(getAllTopicRisks(providerRisk).map(t => [t.sustainabilityTopicId, t]))
}

function getChangedTopicNames(
  current: SustainabilityTopicProviderRisk,
  previous: SustainabilityTopicProviderRisk | null,
): string[] {
  return extractUniqueTopicNames(filterTopics(current, previous, hasRiskStatusChanged))
}

// ============================================================================
// Risk Index Detection
// ============================================================================

function findToggledRiskIndex(
  current: SustainabilityTopicProviderRisk,
  previous: SustainabilityTopicProviderRisk,
): ChangedRiskIndex | null {
  const currentTopics = getAllTopicRisks(current)
  const previousTopicsMap = buildTopicMap(previous)

  for (const topic of currentTopics) {
    const prevTopic = previousTopicsMap.get(topic.sustainabilityTopicId)
    if (!prevTopic) continue

    const countryToggle = findToggledRisk(
      topic.countryRisks ?? [],
      prevTopic.countryRisks ?? [],
      r => r.riskTypeId,
      r => r.riskType?.name,
      true,
    )
    if (countryToggle) return countryToggle

    const industryToggle = findToggledRisk(
      topic.industryRisks ?? [],
      prevTopic.industryRisks ?? [],
      r => r.riskTypeId,
      r => r.riskType?.name,
      false,
    )
    if (industryToggle) return industryToggle
  }

  return null
}

function findUpdatedRiskIndex(
  current: SustainabilityTopicProviderRisk,
  previous: SustainabilityTopicProviderRisk,
): ChangedRiskIndex | null {
  const currentTopics = getAllTopicRisks(current)
  const previousTopicsMap = buildTopicMap(previous)

  for (const topic of currentTopics) {
    const prevTopic = previousTopicsMap.get(topic.sustainabilityTopicId)
    if (!prevTopic) continue

    const countryUpdate = findUpdatedRisk(
      topic.countryRisks ?? [],
      prevTopic.countryRisks ?? [],
      r => r.riskTypeId,
      r => r.countryId,
      r => r.riskStatus,
      r => r.riskType?.name,
      true,
    )
    if (countryUpdate) return countryUpdate

    const industryUpdate = findUpdatedRisk(
      topic.industryRisks ?? [],
      prevTopic.industryRisks ?? [],
      r => r.riskTypeId,
      r => r.subNaceCodeId,
      r => r.riskStatus,
      r => r.riskType?.name,
      false,
    )
    if (industryUpdate) return industryUpdate
  }

  return null
}

function findToggledRisk<T>(
  currentRisks: T[],
  previousRisks: T[],
  getId: (r: T) => number,
  getName: (r: T) => string | undefined,
  isCountryRisk: boolean,
): ChangedRiskIndex | null {
  const currentIds = new Set(currentRisks.map(getId))
  const previousIds = new Set(previousRisks.map(getId))

  // Check for newly added
  for (const risk of currentRisks) {
    if (!previousIds.has(getId(risk))) {
      return { riskTypeName: getName(risk), isCountryRisk }
    }
  }

  // Check for removed
  for (const risk of previousRisks) {
    if (!currentIds.has(getId(risk))) {
      return { riskTypeName: getName(risk), isCountryRisk }
    }
  }

  return null
}

function findUpdatedRisk<T>(
  currentRisks: T[],
  previousRisks: T[],
  getId: (r: T) => number,
  getEntityId: (r: T) => number,
  getStatus: (r: T) => unknown,
  getName: (r: T) => string | undefined,
  isCountryRisk: boolean,
): ChangedRiskIndex | null {
  const previousMap = new Map(previousRisks.map(r => [getId(r), r]))

  for (const risk of currentRisks) {
    const prev = previousMap.get(getId(risk))
    // Only an update if: existed before, same entity (country/industry), different status
    if (prev && getEntityId(prev) === getEntityId(risk) && getStatus(prev) !== getStatus(risk)) {
      return { riskTypeName: getName(risk), isCountryRisk }
    }
  }

  return null
}

// ============================================================================
// Organization Change Detection
// ============================================================================

function detectOrganizationChange(
  current: SustainabilityTopicProviderRisk,
  previous: SustainabilityTopicProviderRisk,
): RiskChangeReason | null {
  const currentTopics = getAllTopicRisks(current)
  const previousTopics = getAllTopicRisks(previous)

  if (currentTopics.length === 0 || previousTopics.length === 0) return null

  const currentCountries = new Set(currentTopics.flatMap(t => t.countryRisks?.map(r => r.countryId) ?? []))
  const previousCountries = new Set(previousTopics.flatMap(t => t.countryRisks?.map(r => r.countryId) ?? []))

  const currentIndustries = new Set(currentTopics.flatMap(t => t.industryRisks?.map(r => r.subNaceCodeId) ?? []))
  const previousIndustries = new Set(previousTopics.flatMap(t => t.industryRisks?.map(r => r.subNaceCodeId) ?? []))

  const countryChanged =
    (currentCountries.size > 0 || previousCountries.size > 0) && !areSetsEqual(currentCountries, previousCountries)
  const industryChanged =
    (currentIndustries.size > 0 || previousIndustries.size > 0) && !areSetsEqual(currentIndustries, previousIndustries)

  if (countryChanged && industryChanged) return RiskChangeReason.CountryAndIndustryUpdated
  if (countryChanged) return RiskChangeReason.CountryUpdated
  if (industryChanged) return RiskChangeReason.IndustryUpdated

  return null
}

function areSetsEqual<T>(set1: Set<T>, set2: Set<T>): boolean {
  if (set1.size !== set2.size) return false
  for (const item of set1) {
    if (!set2.has(item)) return false
  }
  return true
}

// ============================================================================
// Assessment Change Analysis
// ============================================================================

interface TopicWithAssessmentChange {
  topicName: string | undefined
  assessmentName: string | undefined
}

function reasonToChangeType(reason: RiskChangeReason): AssessmentChange['type'] {
  switch (reason) {
    case RiskChangeReason.AssessmentEnabled:
      return 'enabled'
    case RiskChangeReason.AssessmentDisabled:
      return 'disabled'
    case RiskChangeReason.AssessmentDeleted:
      return 'deleted'
    case RiskChangeReason.AssessmentCalculated:
      return 'calculated'
    default:
      return 'none'
  }
}

/**
 * Finds topics with assessment changes matching the given reason.
 * Returns the topic name and the name of the assessment that changed.
 */
function getTopicsWithAssessmentChange(
  current: SustainabilityTopicProviderRisk,
  previous: SustainabilityTopicProviderRisk | null,
  reason: RiskChangeReason,
): TopicWithAssessmentChange[] {
  const currentTopics = getAllTopicRisks(current)
  const previousTopicsMap = buildTopicMap(previous)
  const targetType = reasonToChangeType(reason)

  const results: TopicWithAssessmentChange[] = []

  for (const topic of currentTopics) {
    const prevTopic = previousTopicsMap.get(topic.sustainabilityTopicId)
    const change = getAssessmentChange(topic, prevTopic)

    if (change.type !== targetType) continue

    // Get assessment name based on which one changed
    const assessmentName =
      change.source === 'section'
        ? topic.assessmentTemplateSection?.assessmentTemplate?.name
        : topic.assessmentTemplate?.name

    results.push({
      topicName: topic.sustainabilityTopic?.name,
      assessmentName,
    })
  }

  return results
}

// ============================================================================
// Data Extraction Helpers
// ============================================================================
